// src/components/HeaderDefault.js

import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo
} from 'react';
import {
  useLocation,
  useNavigate,
  matchPath,
  Link,
  useParams
} from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import { IoIosArrowBack } from 'react-icons/io';
import API from '../api/client';
import './Header.css';

import { AuthContext } from '../context/AuthContext';
import { ScrollContext } from '../context/ScrollContext';
import { TabContext } from '../context/TabContext';
import { ModalContext } from '../context/ModalContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';

import HomeDropdown from './HomeDropdown';
import ProfessionalDropdown from './ProfessionalDropdown';
import { renderGenderIcon } from '../utils/genderIcons';

export default function HeaderDefault({ forceVisible = false, toggleFilterHeader }) {
  const location = useLocation();
  const navigate = useNavigate();

  const { isAuthenticated, logout } = useContext(AuthContext);
  const { homeScrollPosition }     = useContext(ScrollContext);
  const { activeTab, setActiveTab }= useContext(TabContext);
  const { modalStacks, closeModal }= useContext(ModalContext);

  // ▶ Paso 3.a: obtenemos los callbacks registrados para el header
  const { getHeaderActions } = useContext(ModalHeaderContext);

  // 1) Memoizamos currentStack para no crear array nuevo cada render
  const currentStack = useMemo(
    () => modalStacks[activeTab] || [],
    [modalStacks, activeTab]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ Paso 3.b: BUSCAMOS “ModalUserDetail” en la pila activa
  const userEntry = currentStack
    .slice().reverse()
    .find(m => m.metadata?.modalType === 'userDetail');
  // “modalMode” (view/edit) lo seguimos leyendo de metadata, para conservar compatibilidad
  const userMode = userEntry?.metadata?.modalMode || 'view';
  // ▶ Sacamos el modalId para “userDetail” (si existe) y sus callbacks
  const userModalId = userEntry?.id || null;
  const headerActions = userModalId != null
    ? getHeaderActions(userModalId)
    : null;
  // headerActions (si no es null) tendrá { onEdit, onCancel, onSave }

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) DETECCIÓN DE “MODAL PROFESIONAL”
  let profName = null, profLogo = null;
  const pm = currentStack.slice().reverse().find(m => m.metadata?.profName);
  if (pm) {
    profName = pm.metadata.profName;
    profLogo = pm.metadata.profLogo;
  }
  // 1️⃣ Matcheo de ruta
  const profMatch = matchPath("/professional/:id", location.pathname);

  // 2️⃣ Sólo tratamos la ruta como “modal” si además tenemos modal=true
  const isProfRouteModal =
    Boolean(profMatch) && location.search.includes("modal=true");

  // 3️⃣ Metadata que indica que es modal (abrido internamente)
  const profMetaEntry = currentStack
    .slice()
    .reverse()
    .find(m => m.metadata?.profId);
  const profMetaId    = profMetaEntry?.metadata.profId;
  const isProfModal = Boolean(
    isProfRouteModal ||       // entré por URL con ?modal=true
    profName ||               // abrí el modal y guardé profName en metadata
    profMetaEntry             // abrí el modal y guardé profId en metadata
  );


  // ─────────────────────────────────────────────────────────────────────────────
  // 3) DETECCIÓN DE “MODAL MASCOTA”
  let petName = null, petSexo = null;
  if (!profName && !profMetaId && !profMatch) {
    const petM = currentStack.slice().reverse()
      .find(m => m.metadata?.petName);
    if (petM) {
      petName = petM.metadata.petName;
      petSexo = petM.metadata.petSexo;
    } else {
      petName = location.state?.petName || null;
      petSexo = location.state?.petSexo || null;
    }
  }
  const petMatch   = matchPath("/pet/:id", location.pathname);
  // Sólo tratar como “pet modal” si *también* hay modal=true
  const isPetRouteModal = petMatch && location.search.includes("modal=true");
  // O bien si la propia pila indica que es un modal
  const petMeta      = currentStack.slice().reverse().find(
    m => m.metadata?.petName || m.metadata?.petId
  );
  const isPetModal = Boolean(isPetRouteModal || petMeta);

  // 3.1) Estados auxiliares para datos traídos desde URL
  const [petFromUrl, setPetFromUrl] = useState(null);
  const [profFromUrl, setProfFromUrl] = useState(null);
  // Extraemos "id" de la URL para pet/prof
  const { id: petParamId }            = useParams();
  const profParamId                   = profMatch?.params.id || null;

  // 3.2) Fetch para mascota si entramos vía URL y no tenemos petName
  useEffect(() => {
    const isPetRoute = Boolean(matchPath("/pet/:id", location.pathname));
    if (isPetRoute && !petName && petParamId) {
      API.get(`/pets/${petParamId}/`)
        .then(res => setPetFromUrl({ nombre: res.data.nombre, sexo: res.data.sexo }))
        .catch(() => setPetFromUrl(null));
    }
  }, [location.pathname, petName, petParamId]);

  // 3.3) Fetch para profesional si entramos vía URL y no tenemos profName
  useEffect(() => {
    const isProfRoute = Boolean(matchPath("/professional/:id", location.pathname));
    if (isProfRoute && !profName && profParamId) {
      API.get(`/profesionales/${profParamId}/`)
        .then(res => setProfFromUrl({ nombre: res.data.nombre, logo: res.data.logo }))
        .catch(() => setProfFromUrl(null));
    }
  }, [location.pathname, profName, profParamId]);

  // 3.4) Consolidamos “petName final” y “petSexo final”
  const finalPetName = petName || petFromUrl?.nombre || null;
  const finalPetSexo = petSexo || petFromUrl?.sexo || null;

  // 3.5) Consolidamos “profName final” y “profLogo final”
  const finalProfName = profName || profFromUrl?.nombre || null;
  const finalProfLogo = profLogo || profFromUrl?.logo   || null;

  // 3.6) Limpiar petFromUrl y profFromUrl si ya no estamos en la ruta correspondiente
  useEffect(() => {
    const isPetRoute  = Boolean(matchPath("/pet/:id", location.pathname));
    const isProfRoute = Boolean(matchPath("/professional/:id", location.pathname));
    if (!isPetRoute)  setPetFromUrl(null);
    if (!isProfRoute) setProfFromUrl(null);
  }, [location.pathname]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) VISIBILIDAD DEL HEADER según scroll/pestaña/modales
  const [visible, setVisible] = useState(true);
  const lastY = useRef(homeScrollPosition);
  const TH    = 70;
  useEffect(() => {
    const y = homeScrollPosition;
    if (y < 5) { setVisible(true); lastY.current = y; return; }
    const d = y - lastY.current;
    if (Math.abs(d) > TH) {
      setVisible(d < 0);
      lastY.current = y;
    }
  }, [homeScrollPosition]);

  const modalsAffect = currentStack.filter(m => !m.keepHeader);
  const headerVisible =
    forceVisible ||
    modalsAffect.length > 0 ||
    activeTab !== 'home' ||
    visible;

  const headerStyle = {
    transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
    opacity:   headerVisible ? 1           : 0,
    transition:'transform 0.3s, opacity 0.3s',
    backgroundColor: headerVisible ? '#fff' : 'transparent'
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5) Manejo del click en el logo para “ir a home / scrollTop”
  const handleLogoClick = () => {
    if (activeTab === 'home') {
      const c = document.querySelector('.home-container');
      (c || window).scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setActiveTab('home');
    }
  };

  // ─── FUNCIONES AUXILIARES DE “BACK” ──────────────────────────────────────
  // Cierra UN modal de la pila actual, sin cambiar de pestaña ni navegar
  const backInCurrentTab = () => {
    if (currentStack.length > 0) {
      closeModal(activeTab);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ Paso 3.c: HEADER PARA “ModalUserDetail”
  if (userEntry) {
    // En este punto, userModalId = userEntry.id
    // headerActions = getHeaderActions(userModalId)
    if (userMode === 'view') {
      // ─── MODO “VIEW” ─────────────────────────────────────────────────────
      return (
        <header className="header" style={headerStyle}>
          <div className="default-header">
            {/* Columna izquierda: botón “back” que cierra el modal */}
            <div className="header-left">
              <button
                className="header-back-button"
                onClick={() => {
                  // Si venimos de URL con modal=true en home, limpiar query
                  if (
                    activeTab === 'home' &&
                    currentStack.length === 1 &&
                    location.search.includes('modal=true')
                  ) {
                    navigate("/", { replace: true });
                  }
                  // Si estamos en likes o simplemente cerramos modal:
                  backInCurrentTab();
                }}
                aria-label="Volver"
              >
                <IoIosArrowBack className="header-back-icon" />
              </button>
            </div>

            {/* Columna central: logo siempre centrado */}
            <div className="header-center">
              <Link to="/" onClick={handleLogoClick}>
                <img src="/logo192.png" alt="Pet_it logo" className="logo" />
              </Link>
            </div>

            {/* Columna derecha: botón “Editar” */}
            <div className="header-right">
              <button
                className="header-action-btn"
                onClick={() => headerActions?.onEdit?.()}
              >
                Editar
              </button>
            </div>
          </div>
        </header>
      );
    } else {
      // ─── MODO “EDIT” ─────────────────────────────────────────────────────
      return (
        <header className="header" style={headerStyle}>
          <div className="default-header">
            {/* Columna izquierda: botón “Cancelar” (vuelve a modo 'view') */}
            <div className="header-left">
              <button
                className="header-back-button"
                onClick={() => headerActions?.onCancel?.()}
                aria-label="Cancelar"
              >
                Cancelar
              </button>
            </div>

            {/* Columna central: logo siempre centrado */}
            <div className="header-center">
              <Link to="/" onClick={handleLogoClick}>
                <img src="/logo192.png" alt="Pet_it logo" className="logo" />
              </Link>
            </div>

            {/* Columna derecha: botón “Guardar” */}
            <div className="header-right">
              <button
                className="header-action-btn"
                onClick={() => headerActions?.onSave?.()}
              >
                Guardar
              </button>
            </div>
          </div>
        </header>
      );
    }
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // ▶ FIN BLOQUE “ModalUserDetail” ──────────────────────────────────────────────


  // ─────────────────────────────────────────────────────────────────────────────
  // A) HEADER para MODAL PROFESIONAL
  if (isProfModal && finalProfName) {
    return (
      <header className="header" style={headerStyle}>
        <div className="header-professional">
          <button
            className="header-back-button"
            onClick={() => backInCurrentTab()}
            aria-label="Volver"
          >
            <IoIosArrowBack className="header-back-icon" />
          </button>
          <div className="header-left">
            <img src={finalProfLogo} alt={finalProfName} className="logoLeft" />
          </div>
          <div className="header-center">
            <h2 className="professional-name-header">{finalProfName}</h2>
          </div>
          <div className="header-right">
            <ProfessionalDropdown
              professional={{
                nombre: finalProfName,
                logo: finalProfLogo,
                id: profParamId || profMetaId,
              }}
            />
          </div>
        </div>
      </header>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // 3) Modal Mascota (ahora en cualquier pestaña)
  if (isPetModal && finalPetName) {
    return (
      <header className="header" style={headerStyle}>
        <div className="header-pet-info">
          <button
            className="header-back-button"
            onClick={() => backInCurrentTab()}
            aria-label="Volver"
          >
            <IoIosArrowBack className="header-back-icon" />
          </button>
          <div className="header-left">
            <img src="/logo192.png" alt="Pet_it Logo" className="logoLeft" />
          </div>
          <div className="header-center">
            <div className="petInfoContainer">
              <h2 className="petName">{finalPetName}</h2>
              <span className="genderIcon">
                {finalPetSexo
                  ? renderGenderIcon(finalPetSexo)
                  : <span className="genderIconPlaceholder" />}
              </span>
            </div>
          </div>
          <div className="header-right" />
        </div>
      </header>
    );
  }

  // ────────────────────────────────────────────────────────────────────────────
  // 4) Header por defecto (Home u otras)
  return (
    <header className="header" style={headerStyle}>
      <div className="default-header">
        {/* Columna izquierda: solo mostramos filtro en home o prof_pets */}
        <div className="header-left">
          {(activeTab === 'home' || activeTab === 'prof_pets') && toggleFilterHeader && (
            <button
              className="btn btn-link filter-toggle"
              onMouseDown={e => e.stopPropagation()}
              onTouchStart={e => e.stopPropagation()}
              onClick={toggleFilterHeader}
            >
              <FaFilter className="filter-icon" />
            </button>
          )}
        </div>

        {/* Columna central: logo siempre centrado */}
        <div className="header-center">
          <Link to="/" onClick={handleLogoClick}>
            <img src="/logo192.png" alt="Pet_it logo" className="logo" />
          </Link>
        </div>

        {/* Columna derecha: dropdown de usuario */}
        <div className="header-right">
          <HomeDropdown isAuthenticated={isAuthenticated} logout={logout} />
        </div>
      </div>
    </header>
  );
}
